import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { API_URL } from "../../lib/utils";
import { Card, CardContent } from "../../components/ui/card";
import { Building2, Loader2, AlertCircle } from "lucide-react";
import { Button } from "../../components/ui/button";

export const PortalAuthPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setPortalSession } = useAuth();
    const [status, setStatus] = useState("verifying"); // verifying, success, error, expired
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
        const token = searchParams.get("token");
        if (!token) {
            setStatus("error");
            setErrorMsg("Aucun lien d'accès fourni.");
            return;
        }

        const verify = async () => {
            try {
                const response = await fetch(`${API_URL}/portal/magic-link/verify?token=${token}`, {
                    method: "POST",
                });

                if (response.status === 410) {
                    setStatus("expired");
                    return;
                }

                if (!response.ok) {
                    const err = await response.json().catch(() => ({}));
                    throw new Error(err.detail || "Lien invalide");
                }

                const data = await response.json();
                setPortalSession(data.access_token, data.user);
                setStatus("success");
                setTimeout(() => navigate("/portal"), 1000);
            } catch (err) {
                setStatus("error");
                setErrorMsg(err.message);
            }
        };

        verify();
    }, [searchParams, navigate, setPortalSession]);

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="portal-auth-page">
            <Card className="w-full max-w-md">
                <CardContent className="pt-8 pb-8 text-center space-y-6">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto">
                        <Building2 className="w-7 h-7 text-primary" />
                    </div>

                    {status === "verifying" && (
                        <>
                            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                            <div>
                                <h2 className="font-heading font-bold text-xl">Vérification en cours...</h2>
                                <p className="text-muted-foreground text-sm mt-2">
                                    Nous vérifions votre lien d'accès
                                </p>
                            </div>
                        </>
                    )}

                    {status === "success" && (
                        <div>
                            <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h2 className="font-heading font-bold text-xl">Accès validé</h2>
                            <p className="text-muted-foreground text-sm mt-2">
                                Redirection vers votre espace client...
                            </p>
                        </div>
                    )}

                    {status === "expired" && (
                        <div>
                            <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
                            <h2 className="font-heading font-bold text-xl">Lien expiré</h2>
                            <p className="text-muted-foreground text-sm mt-2">
                                Ce lien d'accès a expiré. Veuillez contacter votre prestataire pour recevoir un nouveau lien.
                            </p>
                        </div>
                    )}

                    {status === "error" && (
                        <div>
                            <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-4" />
                            <h2 className="font-heading font-bold text-xl">Erreur d'accès</h2>
                            <p className="text-muted-foreground text-sm mt-2">{errorMsg}</p>
                            <Button variant="outline" className="mt-4" onClick={() => navigate("/login")}>
                                Retour à l'accueil
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
